import { useEffect, useState } from "react";
import api from "@/lib/api";
import { rupiah, formatWIB, wibNowLocal, wibLocalToUTCISO, toNum } from "@/lib/format";
import { DataTable } from "@/components/DataTable";
import { MoneyInput } from "@/components/MoneyInput";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { CustomerForm } from "@/pages/Customers";
import { toast } from "sonner";
import { confirmDialog } from "@/lib/confirm";
import { Plus, HandCoins, Trash2, UserPlus, Undo2, PiggyBank, History } from "lucide-react";

export default function Titipan() {
  const [list, setList] = useState([]);
  const [summary, setSummary] = useState({});
  const [openForm, setOpenForm] = useState(false);
  const [returnT, setReturnT] = useState(null);
  const [histT, setHistT] = useState(null);

  const load = () => {
    Promise.all([api.get("/titipan"), api.get("/titipan/summary")]).then(([l, s]) => { setList(l.data); setSummary(s.data); });
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!(await confirmDialog({ title: "Hapus data titipan ini?" }))) return;
    await api.delete(`/titipan/${id}`); toast.success("Titipan dihapus"); load();
  };

  const columns = [
    { key: "created_at", label: "Tanggal & Jam", sortable: true, sortValue: (r) => r.created_at || "", render: (r) => <span className="text-sm">{formatWIB(r.created_at)}</span> },
    { key: "name", label: "Nama Penitip", sortable: true, render: (r) => <span className="font-medium">{r.name}</span> },
    { key: "phone", label: "HP", render: (r) => <span className="text-xs text-muted-foreground">{r.phone || "-"}</span> },
    { key: "initial_amount", label: "Jumlah Titip", sortable: true, align: "right", sortValue: (r) => Number(r.initial_amount || 0), render: (r) => <span className="money text-muted-foreground">{rupiah(r.initial_amount)}</span> },
    { key: "remaining_amount", label: "Masih Dititip", sortable: true, align: "right", sortValue: (r) => Number(r.remaining_amount || 0), render: (r) => <span className="money font-semibold text-amber-600 dark:text-amber-400">{rupiah(r.remaining_amount)}</span> },
    { key: "status", label: "Status", sortable: true, filter: true, render: (r) => <Badge variant={r.remaining_amount > 0 ? "secondary" : "default"} className={r.remaining_amount <= 0 ? "bg-emerald-500 hover:bg-emerald-500" : ""}>{r.remaining_amount > 0 ? "Dititip" : "Diambil"}</Badge> },
    { key: "notes", label: "Catatan", render: (r) => <span className="text-xs text-muted-foreground">{r.notes || "-"}</span> },
    { key: "_act", label: "", render: (r) => (
      <div className="flex gap-1 justify-end">
        <Button data-testid={`btn-return-${r.id}`} size="sm" variant="outline" onClick={() => setReturnT(r)} disabled={r.remaining_amount <= 0} className="gap-1"><Undo2 className="h-3.5 w-3.5" /> Ambil</Button>
        <Button data-testid={`btn-hist-titipan-${r.id}`} size="icon" variant="ghost" onClick={() => setHistT(r)} title="Riwayat pengambilan"><History className="h-3.5 w-3.5" /></Button>
        <Button size="icon" variant="ghost" data-testid={`btn-del-titipan-${r.id}`} onClick={() => del(r.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
      </div>
    ) },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Uang Titipan Orang</p>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">Titipan</h1>
        </div>
        <Button data-testid="btn-new-titipan" onClick={() => setOpenForm(true)} className="gap-2 h-11"><Plus className="h-4 w-4" /> Terima Titipan</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="p-4 bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-800 sm:col-span-2">
          <p className="text-xs uppercase tracking-widest text-amber-700 dark:text-amber-400 font-semibold flex items-center gap-1.5"><PiggyBank className="h-3.5 w-3.5" /> Total Titipan Aktif (dikurangi dari saldo harian)</p>
          <p className="mt-1 money text-2xl font-bold text-amber-700 dark:text-amber-400 tracking-tight" data-testid="titipan-total-aktif">{rupiah(summary.total_aktif || 0)}</p>
        </Card>
        <Card className="p-4"><p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Jumlah Penitip Aktif</p><p className="mt-1.5 text-2xl font-semibold">{summary.jumlah_aktif || 0}</p></Card>
      </div>

      <Card className="p-4">
        <DataTable columns={columns} rows={list} testid="titipan-table" emptyText="Belum ada uang titipan" />
      </Card>

      <TitipanForm open={openForm} onClose={() => setOpenForm(false)} onSaved={load} />
      <ReturnDialog titipan={returnT} onClose={() => setReturnT(null)} onSaved={load} />
      <TitipanHistoryDialog titipan={histT} onClose={() => setHistT(null)} />
    </div>
  );
}

function TitipanForm({ open, onClose, onSaved }) {
  const empty = { customer_id: "", amount: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [dt, setDt] = useState(wibNowLocal());
  const [customers, setCustomers] = useState([]);
  const [openCust, setOpenCust] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadCustomers = () => api.get("/customers").then((r) => setCustomers(r.data));
  useEffect(() => { if (open) { setForm(empty); setDt(wibNowLocal()); loadCustomers(); } /* eslint-disable-next-line */ }, [open]);
  const selected = customers.find((c) => c.id === form.customer_id);

  const save = async () => {
    if (!form.customer_id) return toast.error("Pilih penitip");
    if (!form.amount) return toast.error("Isi jumlah titipan");
    setSaving(true);
    try {
      await api.post("/titipan", { customer_id: form.customer_id, name: selected?.name || "", amount: toNum(form.amount), notes: form.notes, date: wibLocalToUTCISO(dt) });
      toast.success("Titipan diterima"); onSaved?.(); onClose();
    } finally { setSaving(false); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => (o ? null : onClose())}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Terima Titipan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Penitip</Label>
              <div className="flex gap-2">
                <Select value={form.customer_id} onValueChange={(v) => set("customer_id", v)}>
                  <SelectTrigger data-testid="titipan-customer" className="flex-1"><SelectValue placeholder="Pilih orang..." /></SelectTrigger>
                  <SelectContent>
                    {customers.length === 0 && <div className="px-2 py-3 text-xs text-muted-foreground">Belum ada pelanggan</div>}
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.phone ? ` · ${c.phone}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" size="icon" data-testid="btn-quick-add-customer" onClick={() => setOpenCust(true)}><UserPlus className="h-4 w-4" /></Button>
              </div>
            </div>
            {selected && <Card className="p-3 bg-accent/40 text-sm"><p className="font-semibold">{selected.name}</p>{selected.phone && <p className="text-xs text-muted-foreground">HP: {selected.phone}</p>}</Card>}
            <div><Label>Jumlah Titipan</Label><MoneyInput data-testid="titipan-amount" value={form.amount} onChange={(v) => set("amount", v)} /></div>
            <div>
              <Label>Tanggal & Jam (WIB)</Label>
              <Input data-testid="titipan-datetime" type="datetime-local" value={dt} onChange={(e) => setDt(e.target.value)} />
            </div>
            <div><Label>Catatan</Label><Textarea data-testid="titipan-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} /></div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={onClose} className="flex-1">Batal</Button>
              <Button data-testid="btn-save-titipan" onClick={save} disabled={saving} className="flex-1">Simpan</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <CustomerForm open={openCust} customer={null} onClose={() => setOpenCust(false)} onSaved={async (saved) => { await loadCustomers(); if (saved?.id) set("customer_id", saved.id); }} />
    </>
  );
}

function TitipanHistoryDialog({ titipan, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!titipan) return;
    setLoading(true);
    api.get(`/titipan/${titipan.id}/returns`).then((r) => setRows(r.data)).catch(() => setRows([])).finally(() => setLoading(false));
  }, [titipan]);
  const totalAmbil = rows.reduce((s, p) => s + Number(p.amount || 0), 0);
  return (
    <Dialog open={!!titipan} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Riwayat Pengambilan: {titipan?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <Card className="p-3"><p className="text-[11px] uppercase text-muted-foreground font-semibold">Titip Awal</p><p className="money text-sm font-semibold">{rupiah(titipan?.initial_amount || 0)}</p></Card>
            <Card className="p-3"><p className="text-[11px] uppercase text-muted-foreground font-semibold">Diambil</p><p className="money text-sm font-semibold text-emerald-600">{rupiah(totalAmbil)}</p></Card>
            <Card className="p-3"><p className="text-[11px] uppercase text-muted-foreground font-semibold">Sisa</p><p className="money text-sm font-semibold text-amber-600">{rupiah(titipan?.remaining_amount || 0)}</p></Card>
          </div>
          {loading ? (
            <p className="text-sm text-muted-foreground text-center py-4">Memuat...</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Belum ada pengambilan.</p>
          ) : (
            <div className="divide-y border rounded-lg" data-testid="titipan-history-list">
              {rows.map((p) => (
                <div key={p.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <div>
                    <p className="font-medium">{formatWIB(p.created_at)}</p>
                    {p.notes && <p className="text-xs text-muted-foreground">{p.notes}</p>}
                  </div>
                  <span className="money font-semibold text-emerald-600 dark:text-emerald-400">-{rupiah(p.amount)}</span>
                </div>
              ))}
            </div>
          )}
          <Button variant="outline" onClick={onClose} className="w-full">Tutup</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReturnDialog({ titipan, onClose, onSaved }) {
  const [amount, setAmount] = useState(0);
  const [notes, setNotes] = useState("");
  useEffect(() => { setAmount(titipan?.remaining_amount || 0); setNotes(""); }, [titipan]);
  const save = async () => {
    if (!amount) return toast.error("Isi nominal");
    try {
      await api.post(`/titipan/${titipan.id}/return`, { amount: toNum(amount), notes });
      toast.success("Titipan diambil/dikembalikan"); onSaved?.(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memproses");
    }
  };
  return (
    <Dialog open={!!titipan} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Ambil Titipan: {titipan?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Card className="p-3 bg-accent/40"><p className="text-xs text-muted-foreground">Masih dititip</p><p className="money text-2xl font-bold text-amber-600 dark:text-amber-400">{rupiah(titipan?.remaining_amount || 0)}</p></Card>
          <div><Label>Jumlah Diambil</Label><MoneyInput data-testid="return-amount" value={amount} onChange={setAmount} /></div>
          <div><Label>Catatan</Label><Input data-testid="return-notes" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">Batal</Button>
            <Button data-testid="btn-save-return" onClick={save} className="flex-1 gap-2"><HandCoins className="h-4 w-4" /> Proses</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
